<template>
  <div>
    <!-- Diálogo principal (criação/edição de clínica) -->
    <v-dialog
      v-model="internalValue"
      max-width="900"
      transition="dialog-bottom-transition"
    >
      <v-card class="ds-dialog">
        <v-card-title class="ds-dialog-header">
          <v-icon color="white" class="mr-2">
            {{ isEdit ? "mdi-pencil" : "mdi-calendar-plus" }}
          </v-icon>
          <span class="ds-dialog-title">
            {{ isEdit ? "Editar Clínica" : "Nova Clínica" }}
          </span>
          <v-spacer />
          <v-btn
            icon
            color="white"
            class="close-btn"
            aria-label="Ver tour"
            @click="startTour"
          >
            <v-icon>mdi-help-circle-outline</v-icon>
          </v-btn>
          <v-btn icon color="white" class="close-btn" @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Stepper para criação de nova clínica (segunda em diante) -->
        <div v-if="shouldShowStepper" data-tour="cr-migracao">
          <v-stepper v-model="currentStep" vertical>
            <!-- Step 1: Escolha de migração -->
            <v-stepper-step
              :complete="currentStep > 1"
              step="1"
              color="primary"
            >
              Migração de Pacientes
              <small>Escolha como transferir seus pacientes</small>
            </v-stepper-step>

            <v-stepper-content step="1">
              <v-card flat class="mb-4">
                <v-card-text>
                  <div class="text-center mb-4">
                    <v-icon large color="primary" class="mb-2">
                      mdi-account-multiple-plus
                    </v-icon>
                    <h3 class="text-h6 mb-2">
                      Como deseja transferir seus pacientes?
                    </h3>
                    <p class="grey--text">
                      Você possui <strong>{{ totalPatients }}</strong> pacientes
                      cadastrados
                    </p>
                  </div>

                  <v-radio-group v-model="migrationChoice" class="mt-4">
                    <v-radio
                      label="Transferir todos os pacientes"
                      value="all"
                      color="primary"
                    >
                      <template #label>
                        <div>
                          <strong>Transferir todos os pacientes</strong>
                          <div class="text-caption grey--text">
                            Todos os {{ totalPatients }} pacientes serão
                            vinculados à nova clínica
                          </div>
                        </div>
                      </template>
                    </v-radio>

                    <v-radio
                      label="Escolher pacientes específicos"
                      value="select"
                      color="primary"
                    >
                      <template #label>
                        <div>
                          <strong>Escolher pacientes específicos</strong>
                          <div class="text-caption grey--text">
                            Selecione individualmente quais pacientes vincular à
                            nova clínica
                          </div>
                        </div>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </v-card-text>
              </v-card>

              <v-btn
                color="primary"
                @click="handleMigrationChoice"
                :disabled="!migrationChoice"
              >
                Continuar
              </v-btn>
            </v-stepper-content>

            <!-- Step 2: Seleção de pacientes -->
            <v-stepper-step
              v-if="migrationChoice === 'select'"
              :complete="currentStep > 2"
              step="2"
              color="primary"
            >
              Seleção de Pacientes
              <small>Escolha quais pacientes transferir</small>
            </v-stepper-step>

            <v-stepper-content v-if="migrationChoice === 'select'" step="2">
              <v-card flat class="mb-4">
                <v-card-text>
                  <div class="d-flex justify-space-between align-center mb-4">
                    <h3 class="text-h6">Selecionar Pacientes</h3>
                    <v-chip small color="primary" outlined>
                      {{ selectedPatients.length }}/{{ patients.length }}
                      selecionados
                    </v-chip>
                  </div>

                  <!-- Ações rápidas -->
                  <div class="mb-4">
                    <v-btn
                      small
                      outlined
                      color="primary"
                      class="mr-2"
                      @click="selectAllPatients"
                    >
                      <v-icon small left>mdi-check-all</v-icon>
                      Selecionar Todos
                    </v-btn>
                    <v-btn
                      small
                      outlined
                      color="grey"
                      @click="unselectAllPatients"
                    >
                      <v-icon small left>mdi-close</v-icon>
                      Desmarcar Todos
                    </v-btn>
                  </div>

                  <!-- Campo de busca -->
                  <v-text-field
                    v-model="patientSearch"
                    label="Buscar paciente..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    outlined
                    dense
                    class="mb-4"
                  />

                  <!-- Lista de pacientes -->
                  <div
                    class="patients-list"
                    style="max-height: 300px; overflow-y: auto"
                  >
                    <v-row v-if="filteredPatients.length">
                      <v-col
                        v-for="patient in filteredPatients"
                        :key="patient.id"
                        cols="12"
                        sm="6"
                        md="4"
                      >
                        <v-card
                          outlined
                          :class="{
                            'primary--text': selectedPatients.includes(
                              patient.id,
                            ),
                          }"
                          @click="togglePatientSelection(patient.id)"
                          style="cursor: pointer"
                          hover
                        >
                          <v-card-text class="pa-3">
                            <div class="d-flex align-center">
                              <v-checkbox
                                :value="selectedPatients.includes(patient.id)"
                                :input-value="
                                  selectedPatients.includes(patient.id)
                                "
                                color="primary"
                                dense
                                class="ma-0 pa-0 mr-3"
                                @click.stop="togglePatientSelection(patient.id)"
                              />
                              <div class="flex-grow-1">
                                <div class="font-weight-medium">
                                  {{ patient.name }}
                                </div>
                                <div class="text-caption grey--text">
                                  {{ patient.email }}
                                </div>
                              </div>
                            </div>
                          </v-card-text>
                        </v-card>
                      </v-col>
                    </v-row>

                    <div v-else class="text-center pa-4">
                      <v-icon large class="grey--text"
                        >mdi-account-search</v-icon
                      >
                      <p class="grey--text mt-2">Nenhum paciente encontrado</p>
                    </div>
                  </div>
                </v-card-text>
              </v-card>

              <div class="d-flex">
                <v-btn text @click="currentStep = 1">Voltar</v-btn>
                <v-spacer />
                <v-btn
                  color="primary"
                  @click="currentStep = 3"
                  :disabled="selectedPatients.length === 0"
                >
                  Continuar ({{ selectedPatients.length }} selecionados)
                </v-btn>
              </div>
            </v-stepper-content>

            <!-- Step 3: Formulário da clínica -->
            <v-stepper-step
              :step="migrationChoice === 'select' ? 3 : 2"
              color="primary"
            >
              Dados da Clínica
              <small>Informações básicas da clínica</small>
            </v-stepper-step>

            <v-stepper-content :step="migrationChoice === 'select' ? 3 : 2">
              <ClinicForm
                @clinic-saved="handleClinicSaved"
                @close="closeDialog"
                :is-edit="false"
                :migration-data="getMigrationData()"
              />

              <div class="d-flex mt-4">
                <v-btn text @click="goToPreviousStep">Voltar</v-btn>
              </div>
            </v-stepper-content>
          </v-stepper>
        </div>

        <!-- Formulário direto para primeira clínica ou edição -->
        <div v-else-if="!initializing">
          <ClinicForm
            v-if="internalValue"
            @clinic-saved="handleClinicSaved"
            @close="closeDialog"
            :is-edit="isEdit"
            :clinic-id="editedClinicId"
            :key="isEdit ? `edit-${editedClinicId}` : 'create'"
          />
        </div>

        <!-- Loading -->
        <div v-else class="pa-10 text-center">
          <v-progress-circular indeterminate color="primary" />
        </div>
      </v-card>
    </v-dialog>

    <!-- Diálogo para autenticação com Google -->
    <v-dialog v-model="showGoogleAuthDialog" max-width="500" scrollable>
      <v-card class="v-dialog-card-border">
        <v-card-title class="headline">
          Vincular Usuário a uma Conta Google
        </v-card-title>
        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section">
            <p class="mb-0">
              Para criar uma clínica, é necessário vincular seu usuário com uma
              conta Google. Isso garante acesso adequado aos recursos da
              plataforma.
            </p>
          </section>
        </v-card-text>
        <v-card-actions class="ds-dialog-actions">
          <v-btn outlined color="primary" @click="showGoogleAuthDialog = false">
            Cancelar
          </v-btn>
          <v-spacer />
          <v-btn color="primary" depressed @click="authenticateUserWithGoogle">
            Vincular Conta Google
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import ClinicForm from "./ClinicForm.vue";
import tourDialogMixin from "@/features/tour/tourDialogMixin";

export default {
  name: "ClinicRegisterAndEditDialog",
  mixins: [tourDialogMixin],
  components: { ClinicForm },
  props: {
    value: { type: Boolean, default: false },
    mode: { type: String, default: "create" }, // "create" | "edit"
    clinicId: { type: Number, default: null },
  },
  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },
  data() {
    return {
      loading: false,
      initializing: false,
      doctorHasGoogleAccount: false,
      authChecked: false,
      isEdit: false,
      editedClinicId: null,
      currentStep: 1,
      clinicCount: 0,
      migrationChoice: null,
      patients: [],
      selectedPatients: [],
      totalPatients: 0,
      patientSearch: "",
      showGoogleAuthDialog: false, // dialog para doctor_solo vincular Google
    };
  },
  computed: {
    tourId() {
      return "dialog-clinica-cadastro";
    },
    filteredPatients() {
      if (!this.patientSearch) return this.patients;
      const search = this.patientSearch.toLowerCase();
      return this.patients.filter(
        (p) =>
          (p.name || "").toLowerCase().includes(search) ||
          (p.email || "").toLowerCase().includes(search),
      );
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isCreate() {
      return this.mode === "create";
    },
    shouldShowStepper() {
      return !this.initializing && !this.isEdit && this.clinicCount > 0;
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.initDialog();
        this.tourOnOpen(); // tour guiado na 1ª vez que abre
      }
    },
  },

  methods: {
    tourReady() {
      return !this.initializing;
    },
    tourSteps() {
      return [
        {
          title: "Cadastro de clínica 🏥",
          description:
            "Aqui você cria uma nova clínica ou edita os dados de uma existente. Vou apontar cada parte do formulário.",
        },
        {
          anchor: "cr-migracao",
          title: "Migração de pacientes",
          description:
            "Ao cadastrar uma clínica adicional, você escolhe se transfere todos os seus pacientes para ela ou apenas alguns selecionados. Esta etapa não aparece na sua primeira clínica.",
        },
        {
          anchor: "cf-info",
          title: "Informações básicas",
          description:
            "Nome da clínica e CNPJ — a identificação principal. O CNPJ é o que permite outras pessoas se associarem a esta clínica.",
        },
        {
          anchor: "cf-cep",
          title: "CEP com preenchimento automático",
          description:
            "Digite o CEP e, ao sair do campo, o endereço (rua, bairro, cidade e estado) é preenchido automaticamente. Depois é só ajustar número e complemento.",
        },
        {
          anchor: "cf-endereco",
          title: "Endereço completo",
          description:
            "Confira e complete o endereço da clínica. Esses dados aparecem no card da clínica e nos documentos (como as prescrições).",
        },
        {
          anchor: "cf-salvar",
          title: "Salvar",
          description:
            "Quando estiver tudo preenchido, salve. A clínica passa a aparecer na sua lista de “Minhas Clínicas”.",
        },
      ];
    },

    async initDialog() {
      this.initializing = true;
      this.resetDialog();

      if (this.mode === "edit") {
        this.isEdit = true;
        this.editedClinicId = this.clinicId;
        this.initializing = false;
        return;
      }

      try {
        await this.checkGoogleAuth();

        if (!this.doctorHasGoogleAccount) {
          this.showGoogleAuthDialog = true;
          return;
        }

        await this.fetchClinicCount();

        if (this.clinicCount > 0) {
          await this.fetchPatients();
        }
      } catch (err) {
        console.error(err);
        this.erroDeRequisicao(err, "Erro ao carregar dados.");
      } finally {
        this.initializing = false;
      }
    },

    async checkGoogleAuth() {
      try {
        const { data } = await http.get("/api/check-google-authentication");
        this.doctorHasGoogleAccount = !!data?.authenticated;
      } catch (error) {
        console.error("Erro ao verificar autenticação:", error);
        this.doctorHasGoogleAccount = false;
      } finally {
        this.authChecked = true;
      }
    },

    async authenticateUserWithGoogle() {
      try {
        const { data } = await http.get("/api/auth/google/redirect");
        if (data?.redirect_url) {
          window.location.assign(data.redirect_url);
          return;
        }
        throw new Error("redirect_url não veio do servidor");
      } catch (error) {
        console.error("Erro durante autenticação com Google:", error);
        this.erroDeRequisicao(error, "Não foi possível iniciar a autenticação com o Google.");
      }
    },

    async fetchClinicCount() {
      const { data } = await http.get("/api/clinics/user-clinic-count");
      this.clinicCount = data?.count || 0;
    },

    async fetchPatients() {
      try {
        // ✅ sem checkMyUserId: pega o usuário logado
        const { data: user } = await http.get("/api/user");
        const userId = user?.id;

        if (!userId) {
          throw new Error(
            "Não foi possível obter o id do usuário em /api/user",
          );
        }

        const { data } = await http.get(
          `/api/relation/patients-by-doctor/${userId}`,
        );

        this.patients = data?.data || [];
        this.totalPatients = this.patients.length;
      } catch (err) {
        console.error("Erro ao buscar pacientes:", err);
        throw err;
      }
    },

    handleMigrationChoice() {
      if (this.migrationChoice === "all") {
        this.selectedPatients = this.patients.map((p) => p.id);
      }
      this.currentStep = 2;
    },

    goToPreviousStep() {
      if (this.migrationChoice === "select" && this.currentStep === 3) {
        this.currentStep = 2;
      } else if (this.currentStep === 2) {
        this.currentStep = 1;
      }
    },

    togglePatientSelection(patientId) {
      const idx = this.selectedPatients.indexOf(patientId);
      if (idx > -1) this.selectedPatients.splice(idx, 1);
      else this.selectedPatients.push(patientId);
    },

    selectAllPatients() {
      this.selectedPatients = this.filteredPatients.map((p) => p.id);
    },

    unselectAllPatients() {
      this.selectedPatients = [];
    },

    getMigrationData() {
      if (this.clinicCount === 0) return { migrate_all: true };
      if (this.migrationChoice === "all") return { migrate_all: true };
      return { patient_ids: this.selectedPatients };
    },

    handleClinicSaved() {
      this.$emit("saved");
      this.closeDialog();

      let msg = this.isEdit
        ? "Clínica atualizada com sucesso!"
        : "Clínica criada com sucesso!";

      if (!this.isEdit) {
        if (this.clinicCount === 0) {
          msg += " Todos os seus pacientes foram transferidos automaticamente.";
        } else if (this.migrationChoice === "all") {
          msg += ` Todos os ${this.totalPatients} pacientes foram transferidos.`;
        } else if (this.selectedPatients.length > 0) {
          msg += ` ${this.selectedPatients.length} pacientes foram transferidos.`;
        }
      }

      this.success(msg);
    },

    resetDialog() {
      this.isEdit = false;
      this.editedClinicId = null;
      this.currentStep = 1;
      this.migrationChoice = null;
      this.selectedPatients = [];
      this.patients = [];
      this.totalPatients = 0;
      this.patientSearch = "";
      this.clinicCount = 0;
      this.showGoogleAuthDialog = false;
      this.authChecked = false;
      this.doctorHasGoogleAccount = false;
    },

    closeDialog() {
      this.$emit("input", false);
      this.$nextTick(() => this.resetDialog());
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.close-btn {
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}

.patients-list {
  border: 1px solid var(--ds-blue-100);
  border-radius: 10px;
  padding: 10px;
  background: var(--ds-blue-50);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(31, 77, 63, 0.08) !important;
}
</style>
